import { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import {
  Building2, Users, GraduationCap, BarChart3, Download,
  LogOut, TrendingUp, BookOpen, AlertTriangle, RefreshCw
} from "lucide-react";
import { API_BASE_URL } from "../../lib/config";

const API_URL = API_BASE_URL;

const DistrictDashboard = () => {
  const [teacher, setTeacher] = useState(null);
  const [analytics, setAnalytics] = useState(null);
  const [loading, setLoading] = useState(true);
  const [exporting, setExporting] = useState(false);
  const navigate = useNavigate();

  const getToken = () => localStorage.getItem("teacherToken");

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    const token = getToken();
    if (!token) { navigate("/district/login"); return; }

    try {
      const headers = { Authorization: `Bearer ${token}` };
      const [meRes, analyticsRes] = await Promise.all([
        fetch(`${API_URL}/teacher/me`, { headers }),
        fetch(`${API_URL}/district/analytics`, { headers }),
      ]);

      if (!meRes.ok) { navigate("/district/login"); return; }

      const meData = await meRes.json();
      if (meData.role !== "district_admin") { navigate("/district/login"); return; }

      const analyticsData = analyticsRes.ok ? await analyticsRes.json() : null;

      setTeacher(meData);
      setAnalytics(analyticsData);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const handleExport = async (format) => {
    setExporting(true);
    try {
      const token = getToken();
      const res = await fetch(`${API_URL}/district/export?format=${format}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = await res.json();

      if (format === "csv") {
        const blob = new Blob([data.data], { type: "text/csv" });
        const url = URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url;
        a.download = data.filename;
        a.click();
        URL.revokeObjectURL(url);
      } else {
        const blob = new Blob([JSON.stringify(data.data, null, 2)], { type: "application/json" });
        const url = URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url;
        a.download = data.filename;
        a.click();
        URL.revokeObjectURL(url);
      }
    } catch (err) {
      console.error("Export failed:", err);
    } finally {
      setExporting(false);
    }
  };

  const handleLogout = () => {
    localStorage.removeItem("teacherToken");
    localStorage.removeItem("teacher");
    navigate("/district/login");
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <div className="animate-spin w-12 h-12 border-4 border-purple-500 border-t-transparent rounded-full" />
      </div>
    );
  }

  const stats = [
    {
      label: "Total Teachers",
      value: analytics?.total_teachers ?? 0,
      icon: GraduationCap,
      color: "bg-indigo-100 text-indigo-600",
    },
    {
      label: "Total Classes",
      value: analytics?.total_classes ?? 0,
      icon: BookOpen,
      color: "bg-blue-100 text-blue-600",
    },
    {
      label: "Total Students",
      value: analytics?.total_students ?? 0,
      icon: Users,
      color: "bg-green-100 text-green-600",
    },
    {
      label: "Avg. Accuracy",
      value: `${analytics?.avg_accuracy ?? 0}%`,
      icon: TrendingUp,
      color: "bg-purple-100 text-purple-600",
    },
  ];

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Header */}
      <header className="bg-gradient-to-r from-purple-700 to-purple-800 text-white py-6">
        <div className="max-w-6xl mx-auto px-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-white/20 rounded-xl flex items-center justify-center">
              <Building2 className="w-5 h-5" />
            </div>
            <div>
              <h1 className="font-heading font-bold text-xl">{teacher?.name || "District Admin"}</h1>
              <p className="text-purple-200 text-sm">{teacher?.district_name || "District Dashboard"}</p>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <button
              onClick={fetchData}
              className="flex items-center gap-2 px-4 py-2 bg-white/10 hover:bg-white/20 rounded-xl text-sm font-medium transition-colors"
            >
              <RefreshCw className="w-4 h-4" /> Refresh
            </button>
            <button
              onClick={handleLogout}
              className="flex items-center gap-2 px-4 py-2 bg-white/10 hover:bg-white/20 rounded-xl text-sm font-medium transition-colors"
            >
              <LogOut className="w-4 h-4" /> Logout
            </button>
          </div>
        </div>
      </header>

      <main className="max-w-6xl mx-auto px-4 py-8 space-y-8">

        {/* Stats */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          {stats.map((s) => (
            <div key={s.label} className="bg-white rounded-2xl border border-slate-200 p-5 flex items-center gap-4">
              <div className={`w-12 h-12 rounded-xl flex items-center justify-center ${s.color}`}>
                <s.icon className="w-6 h-6" />
              </div>
              <div>
                <p className="text-2xl font-bold text-slate-800">{s.value}</p>
                <p className="text-slate-500 text-sm">{s.label}</p>
              </div>
            </div>
          ))}
        </div>

        {/* Export Reports */}
        <div className="bg-white rounded-2xl border border-slate-200 p-6">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-2">
              <Download className="w-5 h-5 text-purple-600" />
              <h2 className="font-heading font-bold text-lg text-slate-800">Export Reports</h2>
            </div>
          </div>
          <p className="text-slate-500 text-sm mb-4">
            Download a summary of all teachers, classes, and student counts in your district.
          </p>
          <div className="flex gap-3">
            <button
              onClick={() => handleExport("csv")}
              disabled={exporting}
              className="flex items-center gap-2 px-5 py-2.5 bg-purple-600 text-white font-semibold rounded-xl hover:bg-purple-700 transition-colors disabled:opacity-50"
            >
              <Download className="w-4 h-4" />
              {exporting ? "Exporting..." : "Export CSV"}
            </button>
            <button
              onClick={() => handleExport("json")}
              disabled={exporting}
              className="flex items-center gap-2 px-5 py-2.5 border-2 border-purple-200 text-purple-700 font-semibold rounded-xl hover:bg-purple-50 transition-colors disabled:opacity-50"
            >
              <Download className="w-4 h-4" />
              Export JSON
            </button>
          </div>
        </div>

        {/* Teachers Table */}
        <div className="bg-white rounded-2xl border border-slate-200 p-6">
          <div className="flex items-center gap-2 mb-5">
            <GraduationCap className="w-5 h-5 text-indigo-600" />
            <h2 className="font-heading font-bold text-lg text-slate-800">Teachers</h2>
          </div>

          {analytics?.teachers?.length === 0 ? (
            <div className="text-center py-10 text-slate-400">
              <Users className="w-10 h-10 mx-auto mb-2 opacity-40" />
              <p>No teachers approved yet.</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-slate-100">
                    <th className="text-left pb-3 text-slate-500 font-semibold">Teacher</th>
                    <th className="text-left pb-3 text-slate-500 font-semibold">School</th>
                    <th className="text-center pb-3 text-slate-500 font-semibold">Classes</th>
                  </tr>
                </thead>
                <tbody>
                  {(analytics?.teachers || []).map((t) => (
                    <tr key={t.id} className="border-b border-slate-50 hover:bg-slate-50 transition-colors">
                      <td className="py-3">
                        <p className="font-semibold text-slate-800">{t.name}</p>
                        <p className="text-slate-400 text-xs">{t.email}</p>
                      </td>
                      <td className="py-3 text-slate-600">{t.school_name || "—"}</td>
                      <td className="py-3 text-center font-bold text-slate-800">{t.class_count}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>

        {/* Activity summary */}
        <div className="bg-white rounded-2xl border border-slate-200 p-6">
          <div className="flex items-center gap-2 mb-4">
            <BarChart3 className="w-5 h-5 text-green-600" />
            <h2 className="font-heading font-bold text-lg text-slate-800">Activity Summary</h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="bg-slate-50 rounded-xl p-4">
              <p className="text-slate-500 text-sm mb-1">Questions Answered District-Wide</p>
              <p className="text-3xl font-bold text-slate-800">
                {(analytics?.total_questions_answered || 0).toLocaleString()}
              </p>
            </div>
            <div className="bg-slate-50 rounded-xl p-4">
              <p className="text-slate-500 text-sm mb-1">District Average Accuracy</p>
              <p className="text-3xl font-bold text-slate-800">
                {analytics?.avg_accuracy ?? 0}%
              </p>
            </div>
          </div>
          {(analytics?.avg_accuracy ?? 0) < 60 && (
            <div className="mt-4 flex items-center gap-2 bg-yellow-50 border border-yellow-200 text-yellow-800 px-4 py-3 rounded-xl text-sm">
              <AlertTriangle className="w-4 h-4 flex-shrink-0" />
              District accuracy is below 60%. Consider coordinating additional support resources with campus administrators.
            </div>
          )}
        </div>
      </main>
    </div>
  );
};

export default DistrictDashboard;
